import { Injectable } from '@angular/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { OperatorFunction, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DataQueryService } from '../../modules/shared-platform/services/data-query/data-query.service';
import { DataQueryServiceConfiguration } from '../../modules/shared-platform/services/data-query/data-query-service-configuration';
import { DataSource } from '../../modules/shared-platform/models/charts/fusion-charts/data-source';
import { CycleFilters } from './models/filters/cycle-filters';
import { EquipmentCountTotals } from './models/equipment-count/equipment-count-totals';
import { EquipmentCountFilters } from './models/filters/equipment-count-filters';
import { EquipmentCountHistoricalTrend } from './models/equipment-count/equipment-count-historical-trend';
import { EquipmentCountSummary } from './models/equipment-count/equipment-count-summary';
import { EquipmentCountHighlightKpi } from './models/equipment-count/equipment-count-highlight-kpi';
import { EquipmentCountHighlightHistoricalKpi } from './models/equipment-count/equipment-count-highlight-historical-kpi';
import { ShipmentsRecentPerformanceKpi } from './models/shipments/shipments-recent-performance-kpi';
import { ShipmentsFilters } from './models/filters/shipments-filters';
import { ShipmentsRecentPerformanceSnapshot } from './models/shipments/shipments-recent-performance-snapshot';
import { ShipmentsHistoricalPerformance } from './models/shipments/shipments-historical-performance';
import { NetworkDwellFilters } from './models/filters/network-dwell-filters';
import { NetworkDwellCount } from './models/network-dwell/network-dwell-count';
import { NetworkDwellExport } from './models/network-dwell/network-dwell-export';
import { NetworkDwellGeographical } from './models/network-dwell/network-dwell-geographical';
import { NetworkDwellGridGeographical } from './models/network-dwell/network-dwell-grid-geographical';
import { NetworkDwellLineChartGeographical } from './models/network-dwell/network-dwell-line-chart-geographical';
import { AssociationGroupings } from './models/association-groupings';
import { RailcarCountComparison } from './models/railstate-compare-contrast/railcar-count-comparison';
import { RailcarTrainVolumnes } from './models/railstate-compare-contrast/railcar-train-volumes';
import { RailcarCarTypeMixes } from './models/railstate-compare-contrast/railcar-car-type-mixes';
import { RailcarTravelTimes } from './models/railstate-compare-contrast/railcar-travel-times';
import { DashboardFilters } from './models/filters/dashboard-filters';
import { Report } from './models/report/report';
import { CycleSegmentChangesSummary } from './models/cycle-time/cycle-segment-changes-summary';
import { CycleTimeHistoricalPerformanceAverageSegmentChart } from './models/cycle/cycle-time-historical-performance-average-segment-chart';
import { CycleTimeRecentlPerformanceSegmentChart } from './models/cycle/CycleTimeRecentlPerformanceSegmentChart';
import { CycleTimeDistanceTrends } from './models/cycle/cycle-time-distance-trends';
import { CycleTimeSegmentMapAndGridByCountry } from './models/cycle/cycle-time-segment-map-grid-by-country';
import { CycleTimeSegmentMapAndGridByRegion } from './models/cycle/cycle-time-segment-map-grid-by-region';
import { CycleTimeSegmentMapAndGridBySubRegion } from './models/cycle/cycle-time-segment-map-grid-by-sub-region';
import { DeliveriesFilters } from './models/filters/deliveries-filters';
import { DeliveriesRecentPerformanceSnapshot } from './models/deliveries/deliveries-recent-performance-snapshot';
import { DeliveriesRecentPerformanceKpi } from './models/deliveries/deliveries-recent-performance-kpi';
import { DeliveriesHistoricalPerformance } from './models/deliveries/deliveries-historical-performance';
import { DwellHighlight } from './models/network-dwell/dwell-highlight';
import { ShipmentDeliveryCountsByMonthAndYear } from './models/railstate-compare-contrast/shipment-delivery-counts';
import { ShipmentDeliveryMonthlyCounts } from './models/railstate-compare-contrast/shipment-delivery-monthly-counts';
import { MonthYearValues } from './models/month-year-values';
import { CycleTimeAveragesByMonth } from './models/cycle-time/cycle-time-averages-by-month';
import { EntityFilterCollection } from '../../modules/admin/models/entity-filter';
import { ParticipationPrerequisites } from './models/forecast/participation-prerequisites';
import { DwellKpi } from './models/network-dwell/dwell-kpi';
import { TimePeriodGroupings } from './models/time-period-groupings';

@Injectable({
    providedIn: 'root',
})
export class RailPerformanceGateway extends DataQueryService {
    // ========== cycle-data.service.ts ==========
    getCycleTimeHighlightKpis(cycleFilters: CycleFilters) {
        return this.post(
            CycleDataServiceConfiguration.CycleTimeHighlightKpis(
                cycleFilters.loaded ?? ['averages'],
            ),
            cycleFilters,
        );
    }

    getCycleTimeHighlightAveragesByMonth(
        cycleFilters: CycleFilters,
        transform: OperatorFunction<
            CycleTimeAveragesByMonth[],
            AssociationGroupings<
                CycleTimeHistoricalPerformanceAverageSegmentChart[]
            >
        >,
    ) {
        return this.postWithTransform(
            CycleDataServiceConfiguration.CycleTimeHighlightAveragesByMonth,
            transform,
            cycleFilters,
        );
    }

    // ========== rsiai-data.service.ts ==========
    getInitialConversation(data: any, conversationId?: any) {
        let url: string;
        if (conversationId) {
            url =
                environment.apiUrl +
                'conversations/' +
                conversationId +
                '/Messages';
        } else {
            url = environment.apiUrl + 'conversations';
        }
        return this.http.post<any>(url, data);
    }

    getConversationMessageRetrey(data: any) {
        let firstMileLastMileReportUrl: string =
            environment.apiUrl +
            'conversations/' +
            data?.conversationId +
            '/Messages/' +
            data?.messageId;
        return this.http.get<any>(firstMileLastMileReportUrl);
    }

    getAttachementQuery(data: any) {
        let firstMileLastMileReportUrl: string =
            environment.apiUrl +
            'conversations/' +
            data?.conversationId +
            '/Messages/' +
            data?.messageId +
            '/attachments/' +
            data?.attachmentId +
            '/query-result';
        return this.http.get<any>(firstMileLastMileReportUrl);
    }

    getConversationwithID(data: any) {
        let firstMileLastMileReportUrl: string =
            environment.apiUrl +
            'conversations/' +
            data?.conversationId +
            '/Messages';
        return this.http.post<any>(firstMileLastMileReportUrl, data);
    }

    // ========== equipment-count-data.service.ts ==========
    getEquipmentCountsHistoricalPerformanceGrid(
        filters: EquipmentCountFilters,
    ) {
        return this.post(
            EquipmentCountDataServiceConfiguration.AggregatedHistoricalEquipmentCountTrends,
            filters,
        );
    }

    getEquipmentAllocationDataExport(filters: EquipmentCountFilters) {
        return this.post(
            EquipmentCountDataServiceConfiguration.EquipmentAllocationDataExport,
            filters,
        );
    }

    getEquipmentCountsRecentPerformanceKpi<T>(
        filters: EquipmentCountFilters,
        transform: OperatorFunction<EquipmentCountTotals, T>,
    ) {
        return this.postWithTransform(
            EquipmentCountDataServiceConfiguration.EquipmentCountPerformanceMetrics,
            transform,
            filters,
        );
    }

    getEquipmentCountsSummary(filters: EquipmentCountFilters) {
        return this.post(
            EquipmentCountDataServiceConfiguration.EquipmentCountSummaries,
            filters,
        );
    }

    getEquipmentCountHighlightsTotalKpis(filters: EquipmentCountFilters) {
        return this.post(
            EquipmentCountDataServiceConfiguration.EquipmentCountHighlightTotalKpis,
            filters,
        );
    }

    getEquipmentCountHighlightsLoadedKpis(filters: EquipmentCountFilters) {
        return this.post(
            EquipmentCountDataServiceConfiguration.EquipmentCountHighlightLoadedKpis,
            filters,
        );
    }

    getEquipmentCountHighlightsEmptyKpis(filters: EquipmentCountFilters) {
        return this.post(
            EquipmentCountDataServiceConfiguration.EquipmentCountHighlightEmptyKpis,
            filters,
        );
    }

    getEquipmentCountHighlightsHistoricalKpis(filters: EquipmentCountFilters) {
        return this.post(
            EquipmentCountDataServiceConfiguration.EquipmentCountHighlightHistoricalKpis,
            filters,
        );
    }

    // ========== Forecast and Demand ==========
    getForecastAndDemandParticipationPrerequisites() {
        return this.get(
            ForecastAndDemandDataServiceConfiguration.ForecastAndDemandParticipationPrerequisites,
        );
    }

    // ========== shipments-data.service.ts ==========
    getShipmentsHistoricalPerformance(
        filters: ShipmentsFilters,
        transform?: OperatorFunction<
            AssociationGroupings<ShipmentsHistoricalPerformance[]>,
            any
        >,
        transformedKey: string = 'chart',
    ) {
        return this.postWithTransformUnique(
            ShipmentsDataServiceConfiguration.shipmentsHistoricalPerformance,
            transformedKey,
            transform,
            filters,
        );
    }

    getShipmentsAllocationDataExport(
        filters: ShipmentsFilters,
        transform?: OperatorFunction<
            AssociationGroupings<ShipmentsHistoricalPerformance[]>,
            any
        >,
        transformedKey: string = 'export',
    ) {
        return this.postWithTransformUnique(
            ShipmentsDataServiceConfiguration.shipmentsAllocationDataExport,
            transformedKey,
            transform,
            filters,
        );
    }

    getShipmentsRecentPerformanceKpi(
        filters: ShipmentsFilters,
        transform?: OperatorFunction<
            AssociationGroupings<ShipmentsRecentPerformanceKpi>,
            any
        >,
    ) {
        return this.postWithTransform(
            ShipmentsDataServiceConfiguration.shipmentsRecentPerformanceKpi,
            transform,
            filters,
        );
    }

    getShipmentsRecentPerformanceSnapshot(
        filters: ShipmentsFilters,
        transform?: OperatorFunction<
            AssociationGroupings<ShipmentsRecentPerformanceSnapshot[]>,
            any
        >,
    ) {
        return this.postWithTransform(
            ShipmentsDataServiceConfiguration.shipmentsRecentPerformanceSnapshot,
            transform,
            filters,
        );
    }

    // ========== network-dwell-data.service.ts ==========
    getNetworkDwellHighlights(
        filters: NetworkDwellFilters,
        transform: OperatorFunction<
            AssociationGroupings<DwellHighlight[]>,
            any
        > = (source$) => source$,
    ) {
        return this.postWithTransform(
            NetworkDwellDataServiceConfiguration.NetworkDwellHighlights,
            transform,
            filters,
        );
    }

    exportNetworkDwellTimeTrends(
        filters: NetworkDwellFilters,
        transform: OperatorFunction<
            AssociationGroupings<NetworkDwellExport[]>,
            any
        >,
    ) {
        return this.postWithTransform(
            NetworkDwellDataServiceConfiguration.ExportNetworkDwellTimeTrends,
            transform,
            filters,
        );
    }

    getNetworkDwellGeographicalAggregation(filters: NetworkDwellFilters) {
        return this.post(
            NetworkDwellDataServiceConfiguration.NetworkDwellGeographicalAggregation,
            filters,
        );
    }

    getNetworkDwellGridGeographicalAggregation(
        filters: NetworkDwellFilters,
        transform: OperatorFunction<
            AssociationGroupings<NetworkDwellGridGeographical[]>,
            any
        >,
    ) {
        return this.postWithTransform(
            NetworkDwellDataServiceConfiguration.NetworkDwellGridGeographicalAggregation,
            transform,
            filters,
        );
    }

    getNetworkDwellLineChartGeographicalAggregation(
        filters: NetworkDwellFilters,
    ) {
        return this.post(
            NetworkDwellDataServiceConfiguration.NetworkDwellLineChartGeographicalAggregation,
            filters,
        );
    }

    getNetworkDwellCount(filters: NetworkDwellFilters) {
        return this.post(
            NetworkDwellDataServiceConfiguration.NetworkDwellCount,
            filters,
        );
    }

    getNetworkDwellLoadedFirstMile(filters: NetworkDwellFilters) {
        return this.post(
            NetworkDwellDataServiceConfiguration.NetworkDwellLoadedFirstMile,
            filters,
        );
    }

    getNetworkDwellLoadedLastMile(filters: NetworkDwellFilters) {
        return this.post(
            NetworkDwellDataServiceConfiguration.NetworkDwellLoadedLastMile,
            filters,
        );
    }

    getNetworkDwellLoadedInTransit(filters: NetworkDwellFilters) {
        return this.post(
            NetworkDwellDataServiceConfiguration.NetworkDwellLoadedInTransit,
            filters,
        );
    }

    getNetworkDwellEmptyFirstMile(filters: NetworkDwellFilters) {
        return this.post(
            NetworkDwellDataServiceConfiguration.NetworkDwellEmptyFirstMile,
            filters,
        );
    }

    getNetworkDwellEmptyInTransit(filters: NetworkDwellFilters) {
        return this.post(
            NetworkDwellDataServiceConfiguration.NetworkDwellEmptyInTransit,
            filters,
        );
    }

    getNetworkDwellEmptyLastMile(filters: NetworkDwellFilters) {
        return this.post(
            NetworkDwellDataServiceConfiguration.NetworkDwellEmptyLastMile,
            filters,
        );
    }

    getNetworkDwellExport(filters: NetworkDwellFilters) {
        return this.postWithoutCache(
            NetworkDwellDataServiceConfiguration.NetworkDwellExport,
            filters,
        );
    }

    // ========== railcar-count-data.service.ts ==========
    getShipmentAndDeliveryCountsByMonthAndYear(filters: DashboardFilters) {
        return this.post(
            RailcarCountDataServiceConfiguration.ShipmentDeliveryCount(
                filters.customers,
            ),
            filters,
        );
    }

    getShipmentsAndDeliveryYearlyComparison(filters: DashboardFilters) {
        return this.post(
            RailcarCountDataServiceConfiguration.ShipmentDeliveryYearlyComparison(
                filters.customers,
            ),
            filters,
        );
    }

    getRailcarCountTable(
        customerGroupName: string,
        transform: OperatorFunction<RailcarCountComparison[], GridDataResult>,
    ) {
        return this.getWithTransform(
            RailcarCountDataServiceConfiguration.RailcarCount,
            transform,
            'table',
            {
                customerGroupName,
            },
        );
    }

    getRailcarCountGraph(
        customerGroupName: string,
        transform: OperatorFunction<RailcarCountComparison[], DataSource>,
    ) {
        return this.getWithTransform(
            RailcarCountDataServiceConfiguration.RailcarCount,
            transform,
            'graph',
            {
                customerGroupName,
            },
        );
    }

    getRailcarTrainVolumeChart1() {
        return this.get(
            RailcarCountDataServiceConfiguration.TrainVolumeMetric1,
        );
    }

    getRailcarTrainVolumeChart2() {
        return this.get(
            RailcarCountDataServiceConfiguration.TrainVolumeMetric2,
        );
    }

    getRailcarTrainVolumeChart3() {
        return this.get(
            RailcarCountDataServiceConfiguration.TrainVolumeMetric3,
        );
    }

    getRailcarTrainVolumeChart4() {
        return this.get(
            RailcarCountDataServiceConfiguration.TrainVolumeMetric4,
        );
    }

    getRailcarTrainVolumeChart5() {
        return this.get(
            RailcarCountDataServiceConfiguration.TrainVolumeMetric5,
        );
    }

    getRailcarTrainVolumeChart6() {
        return this.get(
            RailcarCountDataServiceConfiguration.TrainVolumeMetric6,
        );
    }

    getRailcarCarTypeMixeseMetric1(
        transform: OperatorFunction<RailcarCarTypeMixes[], any>,
    ) {
        return this.getWithTransform(
            RailcarCountDataServiceConfiguration.CarTypeMixesMetric1,
            transform,
            'CarTypeMixesMetric1',
        );
    }

    getRailcarCarTypeMixeseMetric2(
        transform: OperatorFunction<RailcarCarTypeMixes[], any>,
    ) {
        return this.getWithTransform(
            RailcarCountDataServiceConfiguration.CarTypeMixesMetric2,
            transform,
            'CarTypeMixesMetric2',
        );
    }

    getRailcarTravelTimesMetric1(
        transform: OperatorFunction<RailcarTravelTimes[], any>,
    ) {
        return this.getWithTransform(
            RailcarCountDataServiceConfiguration.TravelTimeMetric1,
            transform,
            'TravelTimeMetric1',
        );
    }

    getRailcarTravelTimesMetric2(
        transform: OperatorFunction<RailcarTravelTimes[], any>,
    ) {
        return this.getWithTransform(
            RailcarCountDataServiceConfiguration.TravelTimeMetric2,
            transform,
            'TravelTimeMetric2',
        );
    }

    // ========== railcar-data.service.ts ==========
    // ========== report-data.service.ts ==========
    getAllForType(type: string) {
        return this.get(ReportDataServiceConfiguration.ReportsAll, { type });
    }

    save(report: Report) {
        return this.http
            .post<number>(`${environment.apiUrl}Report`, report)
            .pipe(
                tap(() => {
                    return this.refetchAnyQuery(
                        ReportDataServiceConfiguration.ReportsAll.key,
                    );
                }),
            );
    }

    delete(id: number) {
        return this.http.delete(`${environment.apiUrl}Report/${id}`).pipe(
            tap(() => {
                return this.refetchAnyQuery(
                    ReportDataServiceConfiguration.ReportsAll.key,
                );
            }),
        );
    }

    // ========== cycle-time-data.service.ts ==========
    getCycleTimeRecentPerformance(filters: CycleFilters) {
        return this.post(
            CycleTimeDataServiceConfiguration.RecentPerformance,
            filters,
        );
    }

    getCycleTimeRecentPerformanceAverageSegmentChart(filters: CycleFilters) {
        return this.post(
            CycleTimeDataServiceConfiguration.RecentPerformanceChart,
            filters,
        );
    }

    getCycleTimeHistoricalPerformanceAverageSegmentData(filters: CycleFilters) {
        return this.post(
            CycleTimeDataServiceConfiguration.cycleTimeHistoricalPerformanceAverageSegmentChart,
            filters,
        );
    }

    getCycleTimeDistanceTrends(filters: CycleFilters) {
        return this.post(
            CycleTimeDataServiceConfiguration.cycleTimeDistanceTrends,
            filters,
        );
    }

    getSegmentMapAndGridByCountry(filters: CycleFilters) {
        return this.post(
            CycleTimeDataServiceConfiguration.SegmentMapAndGridByCountry,
            filters,
        );
    }

    getSegmentMapAndGridByRegion(filters: CycleFilters) {
        return this.post(
            CycleTimeDataServiceConfiguration.SegmentMapAndGridByRegion,
            filters,
        );
    }

    getSegmentMapAndGridBySubRegion(filters: CycleFilters) {
        return this.post(
            CycleTimeDataServiceConfiguration.SegmentMapAndGridBySubRegion,
            filters,
        );
    }

    // ========== dashboard-deliveries-data.service.ts ==========
    getDeliveriesHistoricalPerformance(
        filters: DeliveriesFilters,
        transform: OperatorFunction<
            AssociationGroupings<DeliveriesHistoricalPerformance[]>,
            any
        >,
    ) {
        return this.postWithTransform(
            DeliveriesDataServiceConfiguration.deliveriesHistoricalPerformance,
            transform,
            filters,
        );
    }

    getDeliveriesAllocationDataExport(
        filters: DeliveriesFilters,
        transform?: OperatorFunction<
            AssociationGroupings<DeliveriesHistoricalPerformance[]>,
            any
        >,
        transformedKey: string = 'export',
    ) {
        return this.postWithTransformUnique(
            DeliveriesDataServiceConfiguration.deliveriesAllocationDataExport,
            transformedKey,
            transform,
            filters,
        );
    }

    getDeliveriesRecentPerformanceKpi(
        filters: DeliveriesFilters,
        transform: OperatorFunction<
            AssociationGroupings<DeliveriesRecentPerformanceKpi>,
            any
        >,
    ) {
        return this.postWithTransform(
            DeliveriesDataServiceConfiguration.deliveriesRecentPerformanceKpi,
            transform,
            filters,
        );
    }

    getDeliveriesRecentPerformanceSnapshot(filters: DeliveriesFilters) {
        return this.post(
            DeliveriesDataServiceConfiguration.deliveriesRecentPerformanceSnapshot,
            filters,
        );
    }
}

// ========== cycle-data.service.ts Configuration ==========
abstract class CycleDataServiceConfiguration {
    static CycleTimeHighlightKpis(
        loadedStatus: string[],
    ): DataQueryServiceConfiguration<MonthYearValues> {
        return new DataQueryServiceConfiguration<MonthYearValues>(
            `cycle-time-highlight-kpis-${loadedStatus.join(',')}`,
            environment.apiUrl + 'Cycles/Highlights',
            0,
        );
    }

    static readonly CycleTimeHighlightAveragesByMonth =
        new DataQueryServiceConfiguration<CycleTimeAveragesByMonth[]>(
            'cycle-time-highlight-averages-by-month',
            environment.apiUrl + 'Cycles/Highlights/AveragesByMonth',
        );
}

// ========== Forecast and Demand Configuration ==========
abstract class ForecastAndDemandDataServiceConfiguration {
    static readonly ForecastAndDemandParticipationPrerequisites =
        new DataQueryServiceConfiguration<ParticipationPrerequisites>(
            'forecast-and-demand-participation-prerequisites',
            environment.apiUrl + 'ForecastDemand/Prerequisites',
            0,
        );
}

// ========== equipment-count-data.service.ts Configuration ==========
abstract class EquipmentCountDataServiceConfiguration {
    static readonly EquipmentCountPerformanceMetrics =
        new DataQueryServiceConfiguration<EquipmentCountTotals>(
            'equipment-counts',
            environment.apiUrl + 'Equipment/Counts',
            0,
        );

    static readonly EquipmentCountSummaries = new DataQueryServiceConfiguration<
        AssociationGroupings<EquipmentCountSummary[]>
    >(
        'equipment-count-summaries',
        environment.apiUrl + 'Equipment/Summaries',
        0,
    );

    static readonly AggregatedHistoricalEquipmentCountTrends =
        new DataQueryServiceConfiguration<
            AssociationGroupings<EquipmentCountHistoricalTrend[]>
        >(
            'equipment-count-aggregated-history',
            environment.apiUrl + 'Equipment/AggregatedHistory',
            0,
        );

    static readonly EquipmentAllocationDataExport =
        new DataQueryServiceConfiguration<
            AssociationGroupings<EquipmentCountHistoricalTrend[]>
        >(
            'equipment-allocation-data-export',
            environment.apiUrl + 'Equipment/EquipmentAllocationDataExport',
            0,
        );

    static readonly EquipmentCountHighlightTotalKpis =
        new DataQueryServiceConfiguration<EquipmentCountHighlightKpi>(
            'equipment-count-highlight-total-kpis',
            environment.apiUrl + 'Equipment/Highlights',
            0,
        );

    static readonly EquipmentCountHighlightLoadedKpis =
        new DataQueryServiceConfiguration<EquipmentCountHighlightKpi>(
            'equipment-count-highlight-loaded-kpis',
            environment.apiUrl + 'Equipment/Highlights',
            0,
        );

    static readonly EquipmentCountHighlightEmptyKpis =
        new DataQueryServiceConfiguration<EquipmentCountHighlightKpi>(
            'equipment-count-highlight-empty-kpis',
            environment.apiUrl + 'Equipment/Highlights',
            0,
        );

    static readonly EquipmentCountHighlightHistoricalKpis =
        new DataQueryServiceConfiguration<
            EquipmentCountHighlightHistoricalKpi[]
        >(
            'equipment-count-highlight-historical-kpis',
            environment.apiUrl + 'Equipment/Highlights/History',
            0,
        );
}

// ========== shipments-data.service.ts Configuration ==========
abstract class ShipmentsDataServiceConfiguration {
    static readonly shipmentsRecentPerformanceKpi =
        new DataQueryServiceConfiguration<
            AssociationGroupings<ShipmentsRecentPerformanceKpi>
        >(
            'shipments-recent-performance-kpi',
            environment.apiUrl + 'Shipments/RecentPerformanceKpi',
            0,
        );

    static readonly shipmentsRecentPerformanceSnapshot =
        new DataQueryServiceConfiguration<
            AssociationGroupings<ShipmentsRecentPerformanceSnapshot[]>
        >(
            'shipments-recent-performance-snapshot',
            environment.apiUrl + 'Shipments/RecentPerformanceSnapshot',
            0,
        );

    static readonly shipmentsHistoricalPerformance =
        new DataQueryServiceConfiguration<
            AssociationGroupings<ShipmentsHistoricalPerformance[]>
        >(
            'shipments-historical-performance',
            environment.apiUrl + 'Shipments/HistoricalPerformance',
            0,
        );

    static readonly shipmentsAllocationDataExport =
        new DataQueryServiceConfiguration<
            AssociationGroupings<ShipmentsHistoricalPerformance[]>
        >(
            'shipments-allocation-data-export',
            environment.apiUrl + 'Shipments/ShipmentsAllocationDataExport',
            0,
        );
}

// ========== network-dwell-data.service.ts Configuration ==========
abstract class NetworkDwellDataServiceConfiguration {
    static readonly NetworkDwellHighlights = new DataQueryServiceConfiguration<
        AssociationGroupings<DwellHighlight[]>
    >(
        'network-dwell-highlights',
        environment.apiUrl + 'NetworkDwell/Average/Highlights',
        0,
    );

    static readonly NetworkDwellGeographicalAggregation =
        new DataQueryServiceConfiguration<
            AssociationGroupings<NetworkDwellGeographical[]>
        >(
            'network-dwell-geographical-aggregation',
            environment.apiUrl + 'NetworkDwell/Average/Geographical',
            0,
        );

    static readonly NetworkDwellGridGeographicalAggregation =
        new DataQueryServiceConfiguration<
            AssociationGroupings<NetworkDwellGridGeographical[]>
        >(
            'network-dwell-grid-geographical-aggregation',
            environment.apiUrl + 'NetworkDwell/Average/GeographicalGrid',
            0,
        );

    static readonly ExportNetworkDwellTimeTrends =
        new DataQueryServiceConfiguration<
            AssociationGroupings<NetworkDwellExport[]>
        >(
            'network-dwell-export-time-trends',
            environment.apiUrl + 'NetworkDwell/Average/ExportTimeTrends',
            0,
        );

    static readonly NetworkDwellLineChartGeographicalAggregation =
        new DataQueryServiceConfiguration<
            AssociationGroupings<NetworkDwellLineChartGeographical[]>
        >(
            'network-dwell-line-chart-geographical-aggregation',
            environment.apiUrl + 'NetworkDwell/Average/GeographicalLineChart',
            0,
        );

    static readonly NetworkDwellCount = new DataQueryServiceConfiguration<
        NetworkDwellCount[]
    >('network-dwell-count', environment.apiUrl + 'NetworkDwell/Count', 0);

    static readonly NetworkDwellLoadedFirstMile =
        new DataQueryServiceConfiguration<
            AssociationGroupings<TimePeriodGroupings<DwellKpi>>
        >(
            'network-dwell-LoadedFirstMile',
            environment.apiUrl + 'NetworkDwell/LoadedFirstMile',
            0,
        );

    static readonly NetworkDwellLoadedLastMile =
        new DataQueryServiceConfiguration<
            AssociationGroupings<TimePeriodGroupings<DwellKpi>>
        >(
            'network-dwell-LoadedLastMile',
            environment.apiUrl + 'NetworkDwell/LoadedLastMile',
            0,
        );

    static readonly NetworkDwellLoadedInTransit =
        new DataQueryServiceConfiguration<
            AssociationGroupings<TimePeriodGroupings<DwellKpi>>
        >(
            'network-dwell-LoadedInTransit',
            environment.apiUrl + 'NetworkDwell/LoadedInTransit',
            0,
        );

    static readonly NetworkDwellEmptyFirstMile =
        new DataQueryServiceConfiguration<
            AssociationGroupings<TimePeriodGroupings<DwellKpi>>
        >(
            'network-dwell-EmptyFirstMile',
            environment.apiUrl + 'NetworkDwell/EmptyFirstMile',
            0,
        );

    static readonly NetworkDwellEmptyLastMile =
        new DataQueryServiceConfiguration<
            AssociationGroupings<TimePeriodGroupings<DwellKpi>>
        >(
            'network-dwell-EmptyLastMile',
            environment.apiUrl + 'NetworkDwell/EmptyLastMile',
            0,
        );

    static readonly NetworkDwellEmptyInTransit =
        new DataQueryServiceConfiguration<
            AssociationGroupings<TimePeriodGroupings<DwellKpi>>
        >(
            'network-dwell-EmptyInTransit',
            environment.apiUrl + 'NetworkDwell/EmptyInTransit',
            0,
        );

    static readonly NetworkDwellExport = new DataQueryServiceConfiguration<
        NetworkDwellExport[]
    >('network-dwell-export', environment.apiUrl + 'NetworkDwell/Export', 0);
}

// ========== railcar-count-data.service.ts Configuration ==========
abstract class RailcarCountDataServiceConfiguration {
    static ShipmentDeliveryCount(
        entityFilters: EntityFilterCollection,
    ): DataQueryServiceConfiguration<ShipmentDeliveryCountsByMonthAndYear> {
        return new DataQueryServiceConfiguration<MonthYearValues>(
            `railcar-count-shipment-delivery-counts-${entityFilters}-${Date.now()}`,
            environment.apiUrl + 'Comparison/ShipmentsAndDeliveries',
            0,
        );
    }

    static ShipmentDeliveryYearlyComparison(
        entityFilters: EntityFilterCollection,
    ): DataQueryServiceConfiguration<ShipmentDeliveryMonthlyCounts[]> {
        return new DataQueryServiceConfiguration<MonthYearValues>(
            `railcar-count-shipment-delivery-comparison-${entityFilters}-${Date.now()}`,
            environment.apiUrl + 'Comparison/ShipmentsAndDeliveries/Yearly',
            0,
        );
    }

    static readonly RailcarCount = new DataQueryServiceConfiguration<
        RailcarCountComparison[]
    >(
        'railcar-count-comparison',
        environment.apiUrl + 'Comparison/Railcar/Count',
    );

    static readonly TrainVolumeMetric1 = new DataQueryServiceConfiguration<
        RailcarTrainVolumnes[]
    >(
        'railcar-train-volume-metric1',
        environment.apiUrl + 'Comparison/Railcar/TrainVolumeMetric1',
    );

    static readonly TrainVolumeMetric2 = new DataQueryServiceConfiguration<
        RailcarTrainVolumnes[]
    >(
        'railcar-train-volume-metric2',
        environment.apiUrl + 'Comparison/Railcar/TrainVolumeMetric2',
    );

    static readonly TrainVolumeMetric3 = new DataQueryServiceConfiguration<
        RailcarTrainVolumnes[]
    >(
        'railcar-train-volume-metric3',
        environment.apiUrl + 'Comparison/Railcar/TrainVolumeMetric3',
    );

    static readonly TrainVolumeMetric4 = new DataQueryServiceConfiguration<
        RailcarTrainVolumnes[]
    >(
        'railcar-train-volume-metric4',
        environment.apiUrl + 'Comparison/Railcar/TrainVolumeMetric4',
    );

    static readonly TrainVolumeMetric5 = new DataQueryServiceConfiguration<
        RailcarTrainVolumnes[]
    >(
        'railcar-train-volume-metric5',
        environment.apiUrl + 'Comparison/Railcar/TrainVolumeMetric5',
    );

    static readonly TrainVolumeMetric6 = new DataQueryServiceConfiguration<
        RailcarTrainVolumnes[]
    >(
        'railcar-train-volume-metric6',
        environment.apiUrl + 'Comparison/Railcar/TrainVolumeMetric6',
    );

    static readonly CarTypeMixesMetric1 = new DataQueryServiceConfiguration<
        RailcarCarTypeMixes[]
    >(
        'railcar-car-type-metric1',
        environment.apiUrl + 'Comparison/Railcar/CarTypeMixesMetric1',
    );

    static readonly CarTypeMixesMetric2 = new DataQueryServiceConfiguration<
        RailcarCarTypeMixes[]
    >(
        'railcar-car-type-metric2',
        environment.apiUrl + 'Comparison/Railcar/CarTypeMixesMetric2',
    );

    static readonly TravelTimeMetric1 = new DataQueryServiceConfiguration<
        RailcarTravelTimes[]
    >(
        'railcar-travel-time-metric1',
        environment.apiUrl + 'Comparison/Railcar/TravelTimeMetric1',
    );

    static readonly TravelTimeMetric2 = new DataQueryServiceConfiguration<
        RailcarTravelTimes[]
    >(
        'railcar-travel-time-metric2',
        environment.apiUrl + 'Comparison/Railcar/TravelTimeMetric2',
    );
}

// ========== report-data.service.ts Configuration ==========
class ReportDataServiceConfiguration {
    static readonly ReportsAll = new DataQueryServiceConfiguration<Report[]>(
        'report',
        environment.apiUrl + 'Report',
    );
}

// ========== cycle-time-data.service.ts Configuration ==========
abstract class CycleTimeDataServiceConfiguration {
    static readonly RecentPerformance = new DataQueryServiceConfiguration<
        AssociationGroupings<CycleSegmentChangesSummary>
    >(
        'cycle-time-recent-performance',
        environment.apiUrl + 'Cycles/SegmentChanges',
        0,
    );

    static readonly RecentPerformanceChart = new DataQueryServiceConfiguration<
        AssociationGroupings<CycleTimeRecentlPerformanceSegmentChart>
    >(
        'cycle-time-recent-performance-chart',
        environment.apiUrl + 'Cycles/RecentPerformanceChart',
        0,
    );

    static readonly cycleTimeHistoricalPerformanceAverageSegmentChart =
        new DataQueryServiceConfiguration<
            AssociationGroupings<
                CycleTimeHistoricalPerformanceAverageSegmentChart[]
            >
        >(
            'cycle-time-historical-performance-average-segment-chart',
            environment.apiUrl + 'Cycles/HistoricalAverageSegmentsChart',
            0,
        );

    static readonly cycleTimeDistanceTrends = new DataQueryServiceConfiguration<
        AssociationGroupings<CycleTimeDistanceTrends[]>
    >(
        'cycle-time-distance-trends',
        environment.apiUrl + 'Cycles/DistanceTrends',
        0,
    );

    static readonly SegmentMapAndGridByCountry =
        new DataQueryServiceConfiguration<
            AssociationGroupings<CycleTimeSegmentMapAndGridByCountry[]>
        >(
            'segment-map-and-grid-by-country',
            environment.apiUrl + 'Cycles/SegmentMapAndGridByCountry',
            0,
        );

    static readonly SegmentMapAndGridByRegion =
        new DataQueryServiceConfiguration<
            AssociationGroupings<CycleTimeSegmentMapAndGridByRegion[]>
        >(
            'segment-map-and-grid-by-region',
            environment.apiUrl + 'Cycles/SegmentMapAndGridByRegion',
            0,
        );

    static readonly SegmentMapAndGridBySubRegion =
        new DataQueryServiceConfiguration<
            AssociationGroupings<CycleTimeSegmentMapAndGridBySubRegion[]>
        >(
            'segment-map-and-grid-by-sub-region',
            environment.apiUrl + 'Cycles/SegmentMapAndGridBySubRegion',
            0,
        );
}

// ========== dashboard-deliveries-data.service.ts Configuration ==========
abstract class DeliveriesDataServiceConfiguration {
    static readonly deliveriesRecentPerformanceKpi =
        new DataQueryServiceConfiguration<
            AssociationGroupings<DeliveriesRecentPerformanceKpi>
        >(
            'Deliveries-recent-performance-kpi',
            environment.apiUrl + 'Deliveries/RecentPerformanceKpi',
            0,
        );

    static readonly deliveriesRecentPerformanceSnapshot =
        new DataQueryServiceConfiguration<
            AssociationGroupings<DeliveriesRecentPerformanceSnapshot[]>
        >(
            'Deliveries-recent-performance-Snapshot',
            environment.apiUrl + 'Deliveries/RecentPerformanceSnapshot',
            0,
        );

    static readonly deliveriesHistoricalPerformance =
        new DataQueryServiceConfiguration<
            AssociationGroupings<DeliveriesHistoricalPerformance[]>
        >(
            'Deliveries-historical-performance',
            environment.apiUrl + 'Deliveries/HistoricalPerformance',
            0,
        );

    static readonly deliveriesAllocationDataExport =
        new DataQueryServiceConfiguration<
            AssociationGroupings<DeliveriesHistoricalPerformance[]>
        >(
            'deliveries-allocation-data-export',
            environment.apiUrl + 'Deliveries/DeliveriesAllocationDataExport',
            0,
        );
}
